import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { authAPI } from '@/api/new-api'
import { CommunicationStatus, CommunicationType } from '@/models'

export type CommunicationHistoryRecipientTypes = {
  userGroupIds?: number[] | null
}

export interface GetCommunicationHistoryRequest {
  buildingId: number | undefined
  skip?: number
  limit?: number
  query?: string
  type?: string[]
  status?: CommunicationStatus
  fromDate?: number
  toDate?: number
}

export interface CommunicationHistoryItem {
  id: number
  title: string
  type: CommunicationType
  buildingId: number
  status: CommunicationStatus
  recipientsCount: number
  createdAt: number
  scheduledDate?: number | null
  message?: string
  image?: string | null
  mailingList?: number | null
  replyTo?: string | null
  from?: string | null
  originalDate?: number | null
  standaloneClientIds?: number[] | null
  buildingNumber?: string | null
  floor?: string | number | null
  yardiPropertyId?: string | null
  targetCount?: number | null
  recipientTypes?: CommunicationHistoryRecipientTypes
  attachments?: { fileName: string; originalName?: string | null }[]
}

export interface GetCommunicationHistoryResponse {
  count: number
  countCompleted: number
  countOngoing: number
  countScheduled: number
  history: CommunicationHistoryItem[]
}

export const getCommunicationHistoryQueryKey = (data: GetCommunicationHistoryRequest) => [
  'useGetCommunicationHistory',
  data,
]

export const getCommunicationHistory = async (
  data: GetCommunicationHistoryRequest,
  signal?: AbortSignal,
): Promise<GetCommunicationHistoryResponse> => {
  const { buildingId, ...queryParams } = data

  const response = await authAPI.get<GetCommunicationHistoryResponse>(
    `/communication/building/${buildingId}/history`,
    {
      params: queryParams,
      signal,
    },
  )

  return response.data
}

export const useGetCommunicationHistory = (data: GetCommunicationHistoryRequest) => {
  return useQuery({
    enabled: !!data.buildingId,
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) => getCommunicationHistory(data, signal),
    queryKey: getCommunicationHistoryQueryKey(data),
  })
}
